import React from "react";
import { Button } from "../../../components";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { getCfmFile } from "../../../redux/slices/CurrentUserSlice";

const IntegrateToolbar = ({ isAdminPage, onBack }: { isAdminPage?: boolean; onBack?: () => void }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.currentUser.currentUser);
  const { currentOffice } = useSelector((state: RootState) => state.office);

  const handleDownloadFile = async () => {
    // const officeId = user?.offices?.[0]?.officeId || currentOffice?.officeId;
    const officeId = currentOffice?.officeId;
    if (officeId) {
      const res = await dispatch(getCfmFile({ officeId: officeId, toolbar: true }));
      if (res?.payload) {
        const url = res.payload as string;

        // Create temp link
        const link = document.createElement("a");
        link.href = url;
        link.download = ""; // empty => browser uses original file name from S3
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    }
  };

  return (
    <div>
      <div className="">
        {onBack && (
          <div className="cursor-pointer hover:opacity-70 transition-opacity mb-3" onClick={onBack}>
            <ArrowBackIcon />
            Back
          </div>
        )}
        <div className="flex justify-between items-center my-3">
          <div className="flex items-center gap-3">
            <h1 className="font-semibold mb-3">Setup & Integration Guide</h1>
          </div>
          <div className="w-fit">
            <Button text="Download .cfm" onClick={handleDownloadFile} />
          </div>
        </div>
      </div>
      <div className="prose prose-sm max-w-none text-gray-700 space-y-4">
        <p>The Docnote PS Suite Toolbar displays key patient information directly within the PS Suite EMR, including:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Whether a patient has an active Docnote account</li>
          <li>Whether the patient is subscribed to your office&apos;s Annual Plan (if applicable)</li>
          <li>A direct link to the patient&apos;s Docnote profile</li>
          <li>A one-click button to open the Docnote custom form within the patient chart</li>
        </ul>
        <p className="italic">
          Note: If you intend to use the custom form alongside the toolbar, ensure you have completed the custom form setup first. Instructions can be
          found in the Integration tab under PSS Custom Form.
        </p>

        <h2 className="font-semibold text-gray-900 mt-6">Step 1 — Download the Toolbar from Docnote</h2>
        <p>
          If you are reading these instructions, you are already on the correct page — simply click the orange <strong>Download .cfm</strong> button
          at the top of the page.
        </p>
        <p>Alternatively, you can navigate here manually:</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>
            Go to <strong>Office Info</strong>.
          </li>
          <li>
            Click the <strong>Integration</strong> tab.
          </li>
          <li>
            Click the <strong>Configure</strong> button next to <strong>PSS Toolbar</strong>.
          </li>
          <li>
            Click <strong>Download .cfm</strong>. The file will be saved to your computer.
          </li>
        </ol>

        <h2 className="font-semibold text-gray-900 mt-6">Step 2 — Import the Toolbar into PS Suite EMR</h2>
        <ol className="list-decimal pl-6 space-y-1">
          <li>Open PS Suite EMR and log in.</li>
          <li>Select the Records window.</li>
          <li>
            Navigate to <strong>Settings &gt; Edit Custom Forms</strong>.
          </li>
          <li>
            You can either drag and drop the downloaded <code>.cfm</code> file directly into this window (then skip to step 8), or continue with steps
            5–7 to import manually.
          </li>
          <li>
            In the forms menu, click <strong>File &gt; Import Form(s)</strong>.
          </li>
          <li>
            Select the <code>.cfm</code> file downloaded from Docnote: <code>Toolbar-[office name][characters]</code>.
          </li>
          <li>
            Click <strong>Choose</strong>.
          </li>
          <li>A confirmation message will indicate the form was successfully imported.</li>
        </ol>

        <h2 className="font-semibold text-gray-900 mt-6">Step 3 — Configure the Reminder in PS Suite EMR</h2>
        <ol className="list-decimal pl-6 space-y-1">
          <li>
            From the Records view, navigate to <strong>Settings &gt; Edit Reminders</strong>.
          </li>
          <li>
            Click <strong>New Reminder</strong>.
          </li>
          <li>
            Enter a name for the reminder (e.g., <em>Provider Name – Docnote Toolbar</em>).
          </li>
          <li>
            Under the action type, select <strong>Show Custom Form or Stamp</strong>.
          </li>
          <li>From the dropdown, select your Docnote Toolbar custom form.</li>
          <li>
            Click <strong>Add Line</strong> to create a search criterion.
          </li>
          <li>
            Under <strong>Other</strong>, set the condition: <strong>Current Doctor Initials → is</strong> → [provider&apos;s initials].
          </li>
          <li>
            Click <strong>Done</strong> to save.
          </li>
        </ol>
        <p>The Docnote Toolbar will now appear automatically in the chart of every patient assigned to the specified physician.</p>
        <p className="italic">
          Note: This reminder must be configured separately for each provider whose patients require toolbar visibility. Repeat Step 3 for each
          applicable provider.
        </p>

        <h2 className="font-semibold text-gray-900 mt-6">Additional Resources</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <a
              href="https://help.pssuiteemr.com/5.23/on/en/Content/04_Custom_forms/Importing_custom_forms.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Official TELUS PS Suite EMR Documentation — Importing Custom Forms
            </a>
          </li>
          <li>
            <a
              href="https://help.pssuiteemr.com/5.23/on/en/Content/04_Custom_forms/Activating_toolbar_custom_forms_with_reminders.htm?Highlight=toolbar%20reminder"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Official TELUS PS Suite EMR Documentation — Activating Toolbar Custom Forms with Reminders
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};
export default IntegrateToolbar;
